import { format } from "date-fns";
import { de } from "date-fns/locale";
import ExcelJS from "exceljs";
import type { FunctionComponent } from "react";

import DownloadExcelButton from "~/components/buttons/DownloadExcelButton";
import type {
	TeamUtilisationMonth,
	TeamUtilisationRow,
} from "~/modules/reports/api/utilisationReport/utilisationReportTypes.ts";
import type {
	ResolvedGroup,
	ResolvedMember,
} from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/TeamUtilisationTable.tsx";

type Props = {
	groups: ResolvedGroup[];
	months: TeamUtilisationMonth[];
	showSalesAndInternal: boolean;
	year: string;
};

const monthLabel = (month: TeamUtilisationMonth) => format(new Date(month.year, month.month - 1), "MMM yy", { locale: de });

const cellFor = (row: TeamUtilisationRow | undefined, month: TeamUtilisationMonth) =>
	row?.cells.find((cell) => cell.year === month.year && cell.month === month.month);

const DownloadTeamUtilisationReportExcelButton: FunctionComponent<Props> = ({ groups, months, showSalesAndInternal, year }) => {
	const generateExcel = async () => {
		const workBookTitle = "Team-Auslastung " + year;
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(workBookTitle);

		const headers = ["Karrierestufe", "Name", "Zeile"];
		months.forEach((month) => {
			headers.push(monthLabel(month) + " Ø");
			headers.push(monthLabel(month) + " Tage");
		});

		const headerRow = worksheet.addRow(headers);
		headerRow.font = { bold: true };
		headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
		headerRow.height = 30;

		const firstMonthColumn = 4;

		const addDataRow = (careerLevelName: string, memberName: string, rowLabel: string, row: TeamUtilisationRow | undefined) => {
			const values: (string | number | null)[] = [careerLevelName, memberName, rowLabel];
			months.forEach((month) => {
				const cell = cellFor(row, month);
				values.push(cell ? cell.averageDaysPerWeek : null);
				values.push(cell ? cell.daysTracked : null);
			});

			const excelRow = worksheet.addRow(values);
			for (let column = firstMonthColumn; column <= headers.length; column++) {
				excelRow.getCell(column).numFmt = "#,##0.00";
			}
		};

		const addMember = (careerLevelName: string, member: ResolvedMember) => {
			addDataRow(careerLevelName, member.name, "Gesamt", member.mainRow);
			member.projectRows.forEach((row) => addDataRow(careerLevelName, member.name, row.label, row));
			if (showSalesAndInternal) {
				addDataRow(careerLevelName, member.name, "Intern", member.internalRow);
				addDataRow(careerLevelName, member.name, "Sales", member.salesRow);
			}
		};

		groups.forEach((group) => {
			group.members.forEach((member) => addMember(group.displayName, member));
		});

		worksheet.columns.forEach((column, columnIndex) => {
			column.width = columnIndex < firstMonthColumn - 1 ? 22 : 12;
		});

		worksheet.views = [
			{ state: "frozen", xSplit: 3, ySplit: 1, topLeftCell: "D2", activeCell: "D2" },
		];
		workbook.calcProperties.fullCalcOnLoad = true;

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
		const url = window.URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = workBookTitle + ".xlsx";
		anchor.click();
		window.URL.revokeObjectURL(url);
	};

	return <DownloadExcelButton theme="white"
								buttonText=""
								size="sm"
								onClick={generateExcel} />;
};

export default DownloadTeamUtilisationReportExcelButton;
